import { useEffect, useState } from 'react';
import { useTransition } from 'react-spring';

import { ASSIGNMENTS_TRAIL } from 'src/constants/animation.constants';
import { Assignment } from 'src/interfaces/assignment.types';
import { getAssignmentContent } from 'src/utils/assignments.utils';

export const useAssignmentListAnimation = (
  assignments: Assignment[],
  isMiddleSchoolView = false,
  isMobileLayout = false,
) => {
  const getItemHeight = () => {
    if (!isMiddleSchoolView) return 172;

    if (isMobileLayout) return 145;

    return 114;
  };

  const getCardstackItemBaseHeight = () => {
    if (!isMiddleSchoolView) return 110;

    return 114;
  };

  const getActivityHeight = () => {
    if (!isMiddleSchoolView) return 100;

    if (isMobileLayout) return 91;

    return 65;
  };

  // assignment item height in styles + indents
  const itemHeight = getItemHeight();
  const cardstackItemBaseHeight = getCardstackItemBaseHeight();
  const activityHeight = getActivityHeight();
  let height = 0;

  const [haveAssignmentsAppeared, setHaveAssignmentsAppeared] = useState(false);

  const transitions = useTransition(
    assignments.map((data) => {
      const resultHeight =
        data.type === 'Cardstack'
          ? cardstackItemBaseHeight + activityHeight * data.content.length
          : itemHeight;
      return {
        ...data,
        y: (height += resultHeight) - resultHeight,
        height: resultHeight,
      };
    }),
    {
      key: (item: Assignment) => {
        // add _mobile-layout to the key to trigger transition and items' height recalculation
        const mobileLayoutKey = isMobileLayout ? '_mobile-layout' : '';

        if (item.type === 'TaughtLessonAssignment')
          return `${item.id}${mobileLayoutKey}`;
        const activityKey =
          getAssignmentContent(item).cardstackKey ??
          getAssignmentContent(item).lessonKey;
        const date = item.startDate ?? item.createdDate;

        const assignmentKey = item.assignmentBk;

        // tied activityKey with date
        // to avoid keys duplication when arrived separated groups of cardstacks of the same lesson
        return `${activityKey}_${assignmentKey}_${date}${mobileLayoutKey}`;
      },
      from: { opacity: 0, transform: 'translateX(50px) scale(0.8)' },
      enter: ({ y, height }) => ({
        y,
        height,
        opacity: 1,
        transform: 'translateX(0) scale(1)',
      }),
      update: ({ y, height }) => ({ y, height }),
      trail: haveAssignmentsAppeared ? 0 : ASSIGNMENTS_TRAIL,
      config: { mass: 1, tension: 140, friction: 18 },
      delay: haveAssignmentsAppeared ? 0 : 500,
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setHaveAssignmentsAppeared(true);
    }, assignments.length * ASSIGNMENTS_TRAIL);

    return () => {
      clearTimeout(timer);
    };
  }, [assignments]);

  return [transitions, height] as const;
};
